import { useCallback, useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import {
  CANAL, METODO_COBRO, OPERACION, PAGO_TEXTO, avisosDe, claseEstado, comunicada, cuentaAtras, esFinal,
  estadoComunicacion, lineaDireccion, sinLoDeLaEntrega,
} from "@/lib/registro";
import { fecha, fechaHora } from "@/lib/format";
import CommunicationActions, { MarcaPruebas } from "@/components/registry/CommunicationActions";
import RegistryPeopleDialog from "@/components/registry/RegistryPeopleDialog";
import RegistryPlacesEditor from "@/components/registry/RegistryPlacesEditor";
import { Problemas } from "@/pages/admin/Registry";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const selectCls = "mt-1 h-10 w-full rounded-md border border-[#DCD8D0] bg-[#F7F5F0] px-3 text-sm";
const PERSONAS = [["holder", "titular", "detail-registry-holder"], ["second_driver", "segundo conductor", "detail-registry-second"]];

function Faltan({ titulo, items, testid }) {
  if (!items || items.length === 0) return <p className="text-xs text-[#2F5538]" data-testid={testid}>{titulo}: no falta nada.</p>;
  return (
    <div data-testid={testid}>
      <div className="text-sm font-medium text-[#9C6512]">{titulo}, falta:</div>
      <ul className="list-disc pl-5 text-sm text-[#5C5C5C]">{items.map((t, i) => <li key={i}>{t}</li>)}</ul>
    </div>
  );
}

function textoPago(p) {
  if (!p) return "Falta saber cómo se cobró el alquiler";
  const cuando = p.paid_at ? ` · ${fecha(p.paid_at)}` : "";
  if (p.card?.last4) return `${String(p.card.brand || "Tarjeta").toUpperCase()} ****${p.card.last4}${cuando}`;
  return `${PAGO_TEXTO[p.method] || p.method}${cuando}`;
}

const nombre = (p) => [p?.first_name, p?.last_name1, p?.last_name2].filter(Boolean).join(" ");
const lugar = (pl) => (pl?.at_establishment === false ? lineaDireccion(pl.address) || "otra dirección" : "en la oficina");
const mayuscula = (t) => t.charAt(0).toUpperCase() + t.slice(1);

// Cabecera de una comunicación: operación, canal, fecha del hecho (con la cuenta atrás si sigue abierta) y estado.
function Cabecera({ c }) {
  const plazo = !esFinal(c) && c.status !== "sent" ? cuentaAtras(c.due_at) : "";
  return (
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <span className="text-sm font-medium">{OPERACION[c.operation] || c.operation} · {CANAL[c.channel] || c.channel}</span>
        {c.event_at && <div className="text-xs text-[#5C5C5C]">{fechaHora(c.event_at)}{plazo ? ` · ${plazo}` : ""}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <MarcaPruebas c={c} />
        <Badge className={`${claseEstado(c)} border-0`}>{estadoComunicacion(c)}</Badge>
      </div>
    </div>
  );
}

// Registro de viajeros en la ficha de la reserva: canal y oficina, el pago que se comunica, lo que falta, los datos
// de las personas y de los lugares, y las comunicaciones con sus acciones.
// - Lo que falta solo de lo que sigue pendiente: la reserva, mientras no se ha entregado ni comunicado (y nunca sin
//   reserva previa); la entrega, mientras no se ha comunicado, y antes de entregar sin lo que pone la propia entrega.
// - Reserva anulada: nada de lo que falta ni editores; las comunicaciones (su anulación), con sus acciones.
// - Sin canal (registro activado y una oficina sin canal, o sin oficina): sin editores de personas ni de lugares, como con
//   el registro desactivado: sin canal no se guarda nada del registro. Se dice el motivo.
// - Registro desactivado: lo ya comunicado, para leer, y lo que queda por hacer a mano (una anulación de antes de
//   desactivarlo), con su instrucción y su acción; si no hay nada de eso, nada. La API no deja editar (409).
// - Cambiar la oficina con algo ya comunicado se confirma: comunica el cambio y volver atrás no lo deshace.
// - Si no se puede leer (p. ej., una empresa sin registro), no se pinta nada ni se avisa.
// - Sin `admin` (el operador): la oficina solo se lee (cambiarla cambia el canal y la autoridad) y las comunicaciones
//   van sin acciones, y sin las instrucciones de esas acciones (`Problemas`, `avisosDe`); los datos de las personas,
//   los lugares y la forma de cobro sí se completan.
export default function RegistryPanel({ reservation, onChanged, admin = true }) {
  const uid = useId();
  const [d, setD] = useState(null);
  const [persona, setPersona] = useState(null); // "holder" | "second_driver" mientras se edita
  const [lugares, setLugares] = useState(false);
  const [oficinaNueva, setOficinaNueva] = useState(null); // la elegida, mientras se confirma
  // Cambia al volver a leer la ficha tras un 409: el diálogo abierto se vuelve a montar con lo guardado.
  const [lectura, setLectura] = useState(0);
  const rid = reservation.id;
  const entregada = Boolean(reservation.handover_data);
  const cargar = useCallback(async () => {
    try {
      setD((await api.get(`/reservations/${rid}/registry`)).data);
    } catch {
      // Sin registro o sin poder leerlo: la ficha de la reserva sigue igual (la entrega también lo calla).
    }
  }, [rid]);
  useEffect(() => { cargar(); }, [cargar, reservation.status, entregada]);
  const releer = useCallback(async () => { await cargar(); setLectura((n) => n + 1); }, [cargar]);

  if (!d) return null;
  const comunicaciones = d.communications || [];
  const hechas = comunicaciones.filter(comunicada);
  const aMano = comunicaciones.filter((c) => c.status === "manual_required");
  if (!d.enabled && hechas.length === 0 && aMano.length === 0) return null;
  const anulada = reservation.status === "cancelled";
  const editable = Boolean(d.enabled) && !anulada;
  const people = d.people || {};
  const missing = d.missing || {};
  const sinBloqueo = (lista) => (lista || []).filter((t) => t !== d.blocked);
  const comunicadaLa = (op) => comunicaciones.some((c) => c.operation === op && comunicada(c));
  const faltaReserva = !d.walk_in && !entregada && !comunicadaLa("reservation");
  const faltaEntrega = !comunicadaLa("delivery");
  const listaEntrega = entregada ? sinBloqueo(missing.delivery) : sinLoDeLaEntrega(sinBloqueo(missing.delivery));
  const avisos = sinBloqueo(d.warnings);
  const cambiar = async (ruta, body, ok) => {
    try {
      setD((await api.put(`/reservations/${rid}/registry/${ruta}`, body)).data);
      toast.success(ok);
      onChanged?.();
    } catch (err) {
      toast.error(formatError(err));
      if (err?.response?.status === 409) cargar(); // p. ej., el registro se ha desactivado mientras tanto
    }
  };
  const guardado = (v) => { setD(v); setPersona(null); setLugares(false); onChanged?.(); };
  const cambiarOficina = (id) => cambiar("establishment", { establishment_id: id }, "Oficina cambiada");
  const pagadoEl = d.external_method?.paid_at;
  let sinCanal = null;
  if (editable && !d.channel && !d.blocked) {
    // El motivo que da la API; si no da ninguno, la oficina (que no tiene canal) o que no hay oficina.
    if (avisos.length > 0) sinCanal = avisos;
    else if (d.establishment) sinCanal = [`La oficina «${d.establishment.name}» no tiene canal del registro de viajeros: no se comunica nada.`];
    else sinCanal = ["La reserva no tiene oficina del registro de viajeros: no se comunica nada."];
  }

  return (
    <section className="bg-white border border-[#DCD8D0] rounded-xl p-5 mb-6 space-y-4" data-testid="detail-registry-panel"
      aria-labelledby={`${uid}-titulo`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={`${uid}-titulo`} className="font-display text-lg font-semibold text-[#1A1A1A]">Registro de viajeros</h3>
        <div className="flex flex-wrap items-center gap-3 text-sm text-[#5C5C5C]">
          <span>{d.channel ? CANAL[d.channel] : "Sin canal"}{d.walk_in ? " · sin reserva previa" : ""}</span>
          <Link to="/admin/registro-viajeros" className="underline hover:text-marca" data-testid="detail-registry-link">
            Ver el registro
          </Link>
        </div>
      </div>

      {!d.enabled && (
        <p className="text-sm text-[#5C5C5C]">
          El registro de viajeros está desactivado: no se comunica nada nuevo ni se editan sus datos (se activa en sus
          ajustes). Esto es lo que ya se comunicó de esta reserva
          {aMano.length > 0 ? " y lo que queda por hacer a mano" : ""}.
        </p>
      )}
      {editable && d.blocked && (
        <p role="alert" className="rounded-lg border border-acento/40 bg-acento/10 px-3 py-2 text-sm text-[#8C3B26]"
          data-testid="detail-registry-blocked">
          {d.blocked}
        </p>
      )}

      {d.enabled && anulada && d.establishment && (
        <p className="text-sm"><span className="font-medium">Oficina:</span> {d.establishment.name}</p>
      )}
      {editable && (
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          {admin ? (
            <div>
              <Label htmlFor={`${uid}-oficina`}>Oficina</Label>
              <select id={`${uid}-oficina`} className={selectCls} value={d.establishment?.id || ""}
                data-testid="detail-registry-establishment"
                onChange={(e) => (hechas.length > 0 ? setOficinaNueva(e.target.value) : cambiarOficina(e.target.value))}>
                {!d.establishment && <option value="" disabled>Elige la oficina</option>}
                {(d.establishments || []).map((e) => (
                  <option key={e.id} value={e.id}>{e.name} · {e.channel ? CANAL[e.channel] : "sin canal"}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <div className="text-sm font-medium">Oficina</div>
              <div className="mt-2" data-testid="detail-registry-establishment-text">{d.establishment?.name || "Sin oficina"}</div>
            </div>
          )}
          <div>
            <div className="text-sm font-medium">Pago que se comunica</div>
            <div className="mt-2" data-testid="detail-registry-payment-text">{textoPago(d.payment)}</div>
            {d.rental_payment_status === "external" && (
              <div className="mt-2">
                <Label htmlFor={`${uid}-cobro`}>¿Cómo se cobró el alquiler?</Label>
                <select id={`${uid}-cobro`} className={selectCls} value={d.external_method?.method || ""}
                  data-testid="detail-registry-payment"
                  onChange={(e) => cambiar("payment", { method: e.target.value, ...(pagadoEl ? { paid_at: pagadoEl } : {}) },
                    "Forma de cobro guardada")}>
                  <option value="" disabled>Elige la forma de cobro</option>
                  {Object.entries(METODO_COBRO).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {sinCanal && sinCanal.map((t, i) => <p key={i} className="text-sm text-[#9C6512]">{t}</p>)}
      {editable && !d.channel && (
        <p className="text-sm text-[#5C5C5C]" data-testid="detail-registry-no-people">
          Sin canal no se guarda ningún dato de las personas para el registro de viajeros.
        </p>
      )}
      {editable && d.channel && (
        <>
          {faltaReserva && (
            <Faltan titulo="Para comunicar la reserva" items={sinBloqueo(missing.reservation)}
              testid="detail-registry-missing-reservation" />
          )}
          {faltaEntrega && <Faltan titulo="Para comunicar la entrega" items={listaEntrega} testid="detail-registry-missing-delivery" />}
          {avisos.map((w, i) => <p key={i} className="text-xs text-[#9C6512]">{w}</p>)}
        </>
      )}

      {editable && d.channel && (
        <div className="space-y-2 text-sm">
          {PERSONAS.map(([rol, quien, testid]) => {
            const p = people[rol];
            const titular = rol === "holder";
            let texto = "ninguno";
            if (p) texto = nombre(p) || "sin nombre";
            else if (titular) texto = "Se usarán los datos de la reserva hasta que los completes";
            else if (d.second_driver_expected) texto = "sin datos (la reserva lleva segundo conductor)";
            let boton = titular ? "Completar" : "Añadir segundo conductor";
            let accesible = titular ? "Completar titular" : "Añadir segundo conductor";
            if (p) { boton = "Editar"; accesible = `Editar ${quien}`; }
            return (
              <div key={rol} className="flex flex-wrap items-center justify-between gap-2" data-testid={testid}>
                <span><span className="font-medium text-[#1A1A1A]">{mayuscula(quien)}:</span> {texto}</span>
                <Button type="button" size="sm" variant="outline" onClick={() => setPersona(rol)} aria-label={accesible}
                  data-testid={`${testid}-btn`}>
                  {boton}
                </Button>
              </div>
            );
          })}
          <div className="flex flex-wrap items-center justify-between gap-2" data-testid="detail-registry-places">
            <span>
              <span className="font-medium text-[#1A1A1A]">Lugares:</span> entrega {lugar(d.places?.pickup)} · devolución{" "}
              {lugar(d.places?.return)}
            </span>
            <Button type="button" size="sm" variant="outline" onClick={() => setLugares(true)} data-testid="detail-registry-places-btn">
              Lugares de entrega y devolución
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-[#1A1A1A]">Comunicaciones</h4>
        {d.enabled && comunicaciones.length === 0 && <p className="text-sm text-[#8A8A8A]">Aún no hay ninguna.</p>}
        {!d.enabled && comunicaciones.filter((c) => comunicada(c) || c.status === "manual_required").map((c) => (
          // Lo comunicado, solo para leer (ni lo que falta ni acciones); lo que queda a mano, con lo que hay que hacer.
          <div key={c.id} className="border border-[#DCD8D0] rounded-lg p-3" data-testid={`detail-registry-com-${c.id}`}>
            <Cabecera c={c} />
            {c.status === "manual_required" && (
              <>
                <Problemas c={c} anulada={anulada || Boolean(c.reservation_cancelled)} admin={admin} />
                <div className="mt-2">
                  <CommunicationActions c={c} onDone={cargar} anulada={anulada || Boolean(c.reservation_cancelled)} admin={admin} />
                </div>
              </>
            )}
          </div>
        ))}
        {d.enabled && comunicaciones.map((c) => {
          // Una alta de los Mossos que llegó sin admitirse no se reintenta si la reserva está anulada: se anula a mano.
          const anuladaC = anulada || Boolean(c.reservation_cancelled);
          const motivo = c.status === "discarded" ? c.discarded_reason : c.status === "superseded" ? c.superseded_reason : "";
          return (
            <div key={c.id} className="border border-[#DCD8D0] rounded-lg p-3" data-testid={`detail-registry-com-${c.id}`}>
              <Cabecera c={c} />
              {motivo && <p className="mt-1 text-xs text-[#5C5C5C]">{motivo}</p>}
              <Problemas c={c} anulada={anuladaC} admin={admin} />
              {avisosDe(c, admin).length > 0 && <ul className="mt-1 text-xs text-[#9C6512]">{avisosDe(c, admin).map((w, i) => <li key={i}>{w}</li>)}</ul>}
              <div className="mt-2"><CommunicationActions c={c} onDone={cargar} anulada={anuladaC} admin={admin} /></div>
            </div>
          );
        })}
      </div>
      {d.retain_until && <p className="text-xs text-[#8A8A8A]">Se conserva hasta el {fecha(d.retain_until)} (Real Decreto 933/2021).</p>}

      <AlertDialog open={Boolean(oficinaNueva)} onOpenChange={(abierto) => { if (!abierto) setOficinaNueva(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cambiar la oficina?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta reserva ya se ha comunicado al registro de viajeros. Al cambiar la oficina se comunica el cambio: en
              SES, con una anulación y un alta nueva; con los Mossos, con una modificación que se hace a mano en Canal
              Empresa. Y volver a la oficina anterior no lo deshace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="detail-registry-establishment-no">Volver</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const id = oficinaNueva; setOficinaNueva(null); cambiarOficina(id); }}
              className="bg-marca hover:bg-marca-hover" data-testid="detail-registry-establishment-ok">
              Cambiar la oficina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editable && d.channel && persona && (
        <RegistryPeopleDialog key={`${persona}-${lectura}`} reservationId={rid} data={d} rol={persona}
          onClose={() => setPersona(null)} onSaved={guardado} onStale={releer} />
      )}
      {editable && d.channel && lugares && (
        <RegistryPlacesEditor key={`lugares-${lectura}`} reservationId={rid} data={d} onClose={() => setLugares(false)}
          onSaved={guardado} onStale={releer} />
      )}
    </section>
  );
}
